import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';

import vtkLineSource from '@kitware/vtk.js/Filters/Sources/LineSource';
import vtkPlaneSource from '@kitware/vtk.js/Filters/Sources/PlaneSource';
import vtkCircleSource from '@kitware/vtk.js/Filters/Sources/CircleSource';
import vtkSpline3D from '@kitware/vtk.js/Common/DataModel/Spline3D';
import vtkContourTriangulator from '@kitware/vtk.js/Filters/General/ContourTriangulator';

import { vec3 } from 'gl-matrix';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { Vector3 } from '@kitware/vtk.js/types';
import vtkPoints from '@kitware/vtk.js/Common/Core/Points';
import vtkCellArray from '@kitware/vtk.js/Common/Core/CellArray';
import vtkProperty from '@kitware/vtk.js/Rendering/Core/Property';

const actorMetaMap = new WeakMap<
  vtkActor,
  {
    center: [number, number, number];
    radius: number;
    normal: vec3;
    source: vtkCircleSource;
    basis?: {
      center: [number, number, number];
      normal: [number, number, number];
      dx: [number, number, number];
      dy: [number, number, number];
    };
  }
>();

export const getCircleMeta = (actor: vtkActor) => {
  return actorMetaMap.get(actor);
};

export const createCircleSource = (
  actor: vtkActor | undefined,
  position: [number, number, number],
  normal: vec3,
  color?: [number, number, number],
  radius: number = 3,
  showBackface: boolean = false,
  basis?: {
    center: [number, number, number];
    normal: [number, number, number];
    dx: [number, number, number];
    dy: [number, number, number];
  },
): vtkActor => {
  const center: [number, number, number] = [...position];
  const faceColor = color ?? [1, 0.6, 0.8];

  const prevMeta = actor ? actorMetaMap.get(actor) : undefined;
  let circleSource: vtkCircleSource;

  if (!actor) {
    circleSource = vtkCircleSource.newInstance({
      center,
      radius,
      lines: true,
      resolution: 32,
    });
    circleSource.setDirection([normal[0], normal[1], normal[2]]);
    const mapper = vtkMapper.newInstance();
    mapper.setInputConnection(circleSource.getOutputPort());

    actor = vtkActor.newInstance();
    actor.setMapper(mapper);

    actor.setScale(-1, 1, 1);
  } else {
    const meta = actorMetaMap.get(actor);
    circleSource = meta?.source as vtkCircleSource;
    if (!circleSource) {
      circleSource = vtkCircleSource.newInstance({
        center,
        radius,
        lines: true,
        resolution: 32,
      });
    }

    circleSource.setCenter(...center);
    circleSource.setRadius(radius);
    circleSource.setDirection([normal[0], normal[1], normal[2]]);
    const mapper = actor.getMapper() as vtkMapper;
    mapper.setInputConnection(circleSource.getOutputPort());
    mapper.modified();
  }

  actor.getProperty().set({
    color: faceColor,
    ambient: 0.4,
    diffuse: 0.6,
    specular: 0.1,
    opacity: 0.3,
    lineWidth: 3,
  });

  if (showBackface) {
    let backface = actor.getBackfaceProperty();

    if (!backface) {
      backface = vtkProperty.newInstance();
      actor.setBackfaceProperty(backface);
    }

    backface.set({
      color: [
        Math.max(faceColor[0] * 1.2, 1),
        Math.max(faceColor[1] * 1.2, 1),
        Math.max(faceColor[2] * 1.2, 1),
      ],
      ambient: 1.0,
      diffuse: 0.0,
      specular: 0.1,
      opacity: 0.45,
      lineWidth: 3,
    });
  }

  actorMetaMap.set(actor, {
    center,
    radius,
    normal,
    source: circleSource,
    basis: basis ?? prevMeta?.basis,
  });

  actor.modified();
  return actor;
};

export const createPlaneSource = (
  position: [number, number, number],
  normal: vec3,
  viewNormal: vec3,
) => {
  const center: [number, number, number] = [...position];
  if (!center) return;
  const planeSource = vtkPlaneSource.newInstance();
  planeSource.setPoint1(10, 0, 0); // Horizontal edge
  planeSource.setPoint2(0, 10, 0); // Vertical edge
  planeSource.setCenter(center);
  planeSource.setNormal([normal[0], normal[1], normal[2]]);
  const mapper = vtkMapper.newInstance();
  mapper.setInputConnection(planeSource.getOutputPort());
  const actor = vtkActor.newInstance();
  actor.setMapper(mapper);
  actor.getProperty().setColor(1, 1, 1);
  actor.getProperty().set({
    color: [1, 1, 1],
    ambient: 0.4,
    diffuse: 0.6,
    specular: 0.1,
    opacity: 0.8,
    lineWidth: 5,
  });
  return actor;
}

export const createSplinePointPolyData = (
  points: Vector3[],
  samplesPerSegment = 20,
): vtkPolyData => {
  const pointLen = points.length;
  const controlPoints: [number, number, number][] = new Array(pointLen);

  for (let i = 0; i < pointLen; i++) {
    const p = points[i];
    controlPoints[i] = [-p[0], p[1], p[2]];
  }

  const spline = vtkSpline3D.newInstance({
    close: false,
    tension: 0,
    continuity: 0,
    bias: 0,
  });

  (
    spline as unknown as {
      computeCoefficients: (pts: [number, number, number][]) => void;
    }
  ).computeCoefficients(controlPoints);

  const segmentCount = pointLen - 1;

  const totalPoints = segmentCount * samplesPerSegment - (segmentCount - 1);

  const sampledSplinePoints = new Float32Array(totalPoints * 3);

  let writeIndex = 0;

  for (let seg = 0; seg < segmentCount; seg++) {
    for (let s = 0; s < samplesPerSegment; s++) {
      if (seg > 0 && s === 0) continue;

      const t = s / (samplesPerSegment - 1);

      const p = spline.getPoint(seg, t);

      sampledSplinePoints[writeIndex++] = p[0];
      sampledSplinePoints[writeIndex++] = p[1];
      sampledSplinePoints[writeIndex++] = p[2];
    }
  }

  const pointCount = totalPoints;

  const lineConnectivity = new Uint32Array(pointCount + 1);

  lineConnectivity[0] = pointCount;

  for (let i = 0; i < pointCount; i++) {
    lineConnectivity[i + 1] = i;
  }

  const vtkPointData = vtkPoints.newInstance();
  vtkPointData.setData(sampledSplinePoints, 3);

  const polyData = vtkPolyData.newInstance();

  polyData.setPoints(vtkPointData);
  polyData.setLines(vtkCellArray.newInstance({ values: lineConnectivity }));

  const triangulator = vtkContourTriangulator.newInstance();
  triangulator.setInputData(polyData);
  triangulator.update();

  const filledPolyData = triangulator.getOutputData();

  return filledPolyData;
}

export const createLineAndPointPolyData = (points: Vector3[]): vtkPolyData => {
  const pointLen = points.length;

  const flat = new Float32Array(pointLen * 3);

  for (let i = 0; i < pointLen; i++) {
    const p = points[i];
    const o = i * 3;

    flat[o] = -p[0];
    flat[o + 1] = p[1];
    flat[o + 2] = p[2];
  }

  const vtkPts = vtkPoints.newInstance();
  vtkPts.setData(flat, 3);

  const polyData = vtkPolyData.newInstance();
  polyData.setPoints(vtkPts);

  // ----- line cell -----
  const lineCell = new Uint32Array(pointLen + 1);
  lineCell[0] = pointLen;

  for (let i = 0; i < pointLen; i++) {
    lineCell[i + 1] = i;
  }

  polyData.setLines(vtkCellArray.newInstance({ values: lineCell }));

  // ----- point cells -----
  const verts = new Uint32Array(pointLen * 2);

  for (let i = 0; i < pointLen; i++) {
    const o = i * 2;
    verts[o] = 1;
    verts[o + 1] = i;
  }

  polyData.setVerts(vtkCellArray.newInstance({ values: verts }));

  return polyData;
}
